import DocNetBaseService from 'src/services/docnet/DocNetBaseService';
import { api } from 'src/pages/APIs';
import { logger } from 'src/utils/logger';

// Re-export canonical types from @types/agents
export type {
  Agent,
  AgentConfig,
  AgentSkill,
  AgentTrigger,
  AgentCreate,
  TriggerCondition as AgentTriggerCondition,
} from 'src/@types/agents';

import type {
  Agent,
  AgentConfig,
  AgentSkill,
  AgentTrigger,
  AgentCreate,
  McpServerInfo,
} from 'src/@types/agents';

export interface AgentUpdate {
  name?: string;
  description?: string;
  system_prompt?: string;
  document_types?: string[];
  skills?: AgentSkill[];
  triggers?: AgentTrigger[];
  tools?: string[];
  mcp_tools?: string[];
  mcp_servers?: Record<string, string[]>;
  config?: Partial<AgentConfig>;
  priority?: number;
  is_enabled?: boolean;
  is_default?: boolean;
  memory_enabled?: boolean;
}

export interface SkillInfo {
  id: string;
  name: string;
  description: string;
  tools?: string[];
  mcp_tools?: Record<string, string[]>;
  config_schema?: Record<string, any>;
}

/**
 * Response shape for both GET and PUT on the custom-instructions
 * endpoints.  ``is_default`` is ``true`` when the value comes from
 * the system agent's base default (no per-org overlay exists, or the
 * overlay is empty); ``false`` when it's the org's own override.
 *
 * The PUT endpoint mirrors GET's effective-state semantics, so saving
 * an empty string on a system agent that has a base default returns
 * the inherited value with ``is_default: true`` — no follow-up GET
 * required.
 */
export interface CustomInstructionsResponse {
  agent_id: string;
  custom_instructions: string;
  is_default: boolean;
}

/**
 * API Service for managing AI Agents via DocNet.
 */
export default class AgentApiService extends DocNetBaseService {
  /**
   * List all agents for the organization.
   */
  public async list(
    skip = 0,
    limit = 100,
    isEnabled?: boolean,
    documentType?: string,
  ): Promise<Agent[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('skip', skip.toString());
    queryParams.append('limit', limit.toString());
    if (isEnabled !== undefined) {
      queryParams.append('is_enabled', isEnabled.toString());
    }
    if (documentType) {
      queryParams.append('document_type', documentType);
    }
    const url = `${api.get_agents}?${queryParams.toString()}`;
    const result = await super.get(url);
    // v2 API wraps agents in {agents: [...], total, skip, limit}
    if (result && typeof result === 'object' && 'agents' in result && Array.isArray(result.agents)) {
      return result.agents;
    }
    // Fallback: if result is already an array
    if (Array.isArray(result)) {
      return result;
    }
    logger.error('AgentApiService.list: unexpected response format', result);
    throw new Error('Unexpected response format from agents API');
  }

  /**
   * Get a single agent by ID.
   */
  public async getAgent(agentId: string): Promise<Agent> {
    return super.get(`${api.get_agent}${agentId}`);
  }

  /**
   * Create a new agent.
   */
  public async create(data: AgentCreate): Promise<Agent> {
    return super.post(api.create_agent, data);
  }

  /**
   * Update an existing agent.
   */
  public async update(agentId: string, data: AgentUpdate): Promise<Agent> {
    return super.put(`${api.update_agent}${agentId}`, data);
  }

  /**
   * Delete an agent.
   */
  public async remove(agentId: string): Promise<void> {
    return super.delete(`${api.delete_agent}${agentId}`, {});
  }

  /**
   * Toggle an agent's enabled status.
   */
  public async toggle(agentId: string, isEnabled: boolean, userId: string): Promise<Agent> {
    const params = new URLSearchParams();
    params.append('is_enabled', String(isEnabled));
    params.append('user_id', userId);
    return super.post(`${api.toggle_agent}${agentId}/toggle?${params.toString()}`, {});
  }

  /**
   * Get custom instructions for an agent (org-specific).
   */
  public async getCustomInstructions(agentId: string): Promise<CustomInstructionsResponse> {
    return super.get(`${api.agent_custom_instructions}${agentId}/custom-instructions`);
  }

  /**
   * Update custom instructions for an agent (org-specific).
   */
  public async updateCustomInstructions(agentId: string, customInstructions: string): Promise<CustomInstructionsResponse> {
    return super.put(`${api.agent_custom_instructions}${agentId}/custom-instructions`, { custom_instructions: customInstructions });
  }

  /**
   * Get personality for an agent (org-specific).
   */
  public async getPersonality(agentId: string): Promise<{ agent_id: string; personality: string | null }> {
    return super.get(`${api.agent_custom_instructions}${agentId}/personality`);
  }

  /**
   * Update personality for an agent (org-specific).
   */
  public async updatePersonality(agentId: string, personality: string | null): Promise<{ agent_id: string; personality: string | null }> {
    return super.put(`${api.agent_custom_instructions}${agentId}/personality`, { personality });
  }

  /**
   * Get approval mode for an agent (org-specific).
   */
  public async getApprovalMode(agentId: string): Promise<{ agent_id: string; approval_mode: string }> {
    return super.get(`${api.agent_custom_instructions}${agentId}/approval-mode`);
  }

  /**
   * Update approval mode for an agent (org-specific).
   */
  public async updateApprovalMode(agentId: string, approvalMode: string): Promise<{ agent_id: string; approval_mode: string }> {
    return super.put(`${api.agent_custom_instructions}${agentId}/approval-mode`, { approval_mode: approvalMode });
  }

  /**
   * Get reasoning effort for an agent (org-specific override).
   */
  public async getReasoningEffort(agentId: string): Promise<{ agent_id: string; reasoning_effort: string }> {
    return super.get(`${api.agent_custom_instructions}${agentId}/reasoning-effort`);
  }

  /**
   * Update reasoning effort for an agent (org-specific). Routes to
   * agent_org_settings for system agents — same path as approval mode and
   * custom instructions — so editing effort on a system agent works.
   */
  public async updateReasoningEffort(agentId: string, effort: string): Promise<{ agent_id: string; reasoning_effort: string }> {
    return super.put(`${api.agent_custom_instructions}${agentId}/reasoning-effort`, { reasoning_effort: effort });
  }

  /**
   * Get effective per-tool approval overrides for an agent.
   *
   * Overrides only take effect under ``approval_mode='critical'``.
   * Under ``all`` and ``none`` the value of every tool is forced by
   * the mode itself (see backend ``hitl.needs_approval``).
   */
  public async getToolApprovals(
    agentId: string,
  ): Promise<{ agent_id: string; tool_approvals: Record<string, boolean> }> {
    return super.get(`${api.agent_custom_instructions}${agentId}/tool-approvals`);
  }

  /**
   * Set a per-tool override for one tool on one agent.
   * ``true`` raises gating, ``false`` lowers it.
   */
  public async setToolApproval(
    agentId: string,
    toolName: string,
    requireApproval: boolean,
  ): Promise<{ agent_id: string; tool_approvals: Record<string, boolean> }> {
    return super.put(
      `${api.agent_custom_instructions}${agentId}/tool-approvals/${encodeURIComponent(toolName)}`,
      { require_approval: requireApproval },
    );
  }

  /**
   * Clear a per-tool override (reverts to default ``CRITICAL_TOOLS`` gating).
   */
  public async deleteToolApproval(
    agentId: string,
    toolName: string,
  ): Promise<{ agent_id: string; tool_approvals: Record<string, boolean> }> {
    return super.delete(
      `${api.agent_custom_instructions}${agentId}/tool-approvals/${encodeURIComponent(toolName)}`,
      {},
    );
  }

  /**
   * Get reports-to for an agent (org-specific for system agents).
   */
  public async getReportsTo(agentId: string): Promise<{ agent_id: string; reports_to: string | string[] | null }> {
    return super.get(`${api.agent_custom_instructions}${agentId}/reports-to`);
  }

  /**
   * Update reports-to for an agent.
   *
   * For system agents the value persists in ``agent_org_settings.custom_config``
   * (per-org), so each org has its own org-chart assignments without
   * affecting the shared agent row. For custom agents the value lands in
   * the agent's own ``agents.config`` JSONB column.
   */
  public async updateReportsTo(
    agentId: string,
    reportsTo: string | string[] | null,
  ): Promise<{ agent_id: string; reports_to: string | string[] | null }> {
    return super.put(`${api.agent_custom_instructions}${agentId}/reports-to`, {
      reports_to: reportsTo,
    });
  }

  /**
   * Get available agent skills (v2 endpoint, Bearer token auth — same
   * as every other agent call here).
   */
  public async getSkills(): Promise<SkillInfo[]> {
    return super.get(api.get_agent_skills);
  }

  /**
   * Get tool-classification metadata (currently: the set of tools
   * flagged ``critical`` — i.e., tools that always require human
   * approval under the default ``critical`` approval mode).
   *
   * Backend is the canonical source.  Frontend caches the result
   * for the session — the list only changes with a backend deploy,
   * so re-fetching per page-render is wasted bandwidth.
   */
  public async getToolMetadata(): Promise<{ critical: string[] }> {
    return super.get(api.get_agent_tool_metadata);
  }

  /**
   * Discover available MCP servers and their tools.
   */
  public async discoverMcpServers(): Promise<McpServerInfo[]> {
    const result = await super.get(api.mcp_discover);
    if (result && typeof result === 'object' && 'servers' in result && Array.isArray(result.servers)) {
      return result.servers;
    }
    if (Array.isArray(result)) {
      return result;
    }
    logger.error('AgentApiService.discoverMcpServers: unexpected response format', result);
    return [];
  }

  // =====================================================
  // Alias methods for store compatibility
  // =====================================================

  /**
   * Get all agents (alias for list with default pagination).
   */
  public async getAll(filters?: {
    is_enabled?: boolean;
    document_type?: string;
  }): Promise<Agent[]> {
    return this.list(
      0,
      100,
      filters?.is_enabled,
      filters?.document_type
    );
  }

  /**
   * Get agent by ID (alias for getAgent).
   */
  public async getById(agentId: string): Promise<Agent> {
    return this.getAgent(agentId);
  }

  /**
   * Delete agent (alias for remove).
   */
  public async deleteAgent(agentId: string): Promise<void> {
    return this.remove(agentId);
  }
}
